import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { TFunction } from 'i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PermissionCatalogItem = {
  resource: string;
  action: string;
  group: string;
  sortOrder: number;
  labelKey: string;
  descriptionKey?: string;
  key: string; // RESOURCE:ACTION
};

type RoleDetail = {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
};

function formatGroupLabel(
  group: string,
  t: TFunction,
) {
  return t(`rbac.perm_group.${group}`, { defaultValue: group });
}

function labelForPermission(
  p: PermissionCatalogItem,
  t: TFunction,
) {
  // Prefer i18n labelKey from backend; fallback to RESOURCE:ACTION
  return t(p.labelKey, { defaultValue: `${p.resource}:${p.action}` });
}

export default function RolePermissionsPage() {
  const { roleId } = useParams<{ roleId: string }>();
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [role, setRole] = useState<RoleDetail | null>(null);
  const [catalog, setCatalog] = useState<PermissionCatalogItem[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const hasHydratedSelection = React.useRef(false);
  const autosaveTimerRef = React.useRef<number | null>(null);
  const successTimerRef = React.useRef<number | null>(null);
  const lastSavedSelectionRef = React.useRef('');

  const grouped = useMemo(() => {
    const groups = new Map<string, PermissionCatalogItem[]>();
    for (const p of catalog) {
      const g = p.group || 'other';
      const arr = groups.get(g) || [];
      arr.push(p);
      groups.set(g, arr);
    }
    for (const [g, arr] of groups.entries()) {
      arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.key.localeCompare(b.key));
      groups.set(g, arr);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalog]);

  const selectedKeys = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => v)
        .map(([k]) => k)
        .sort(),
    [selected],
  );
  const selectedSignature = useMemo(
    () => selectedKeys.join('\u0000'),
    [selectedKeys],
  );

  const filteredGrouped = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return grouped;
    return grouped
      .map(([groupKey, perms]) => [
        groupKey,
        perms.filter((p) => {
          const label = labelForPermission(p, t).toLowerCase();
          const key = p.key.toLowerCase();
          const resource = p.resource.toLowerCase();
          const action = p.action.toLowerCase();
          return (
            label.includes(q)
            || key.includes(q)
            || resource.includes(q)
            || action.includes(q)
          );
        }),
      ] as [string, PermissionCatalogItem[]])
      .filter(([, perms]) => perms.length > 0);
  }, [grouped, searchQuery, t]);

  useEffect(() => {
    setCollapsedGroups((prev) => {
      const next = { ...prev };
      for (const [groupKey] of grouped) {
        if (next[groupKey] === undefined) {
          next[groupKey] = true; // collapsed by default
        }
      }
      return next;
    });
  }, [grouped]);

  useEffect(() => {
    (async () => {
      if (!roleId) return;
      setLoading(true);
      setError(null);
      try {
        const [roleRes, catRes, permsRes] = await Promise.all([
          authorizedFetch(`${API_BASE}/rbac/roles/${roleId}`),
          authorizedFetch(`${API_BASE}/rbac/permissions`),
          authorizedFetch(`${API_BASE}/rbac/roles/${roleId}/permissions`),
        ]);
        if (!roleRes.ok) throw new Error(t('backoffice.roles_load_error', 'Unable to load role.'));
        if (!catRes.ok) throw new Error(t('backoffice.roles_perm_catalog_error', 'Unable to load permission catalog.'));
        if (!permsRes.ok) throw new Error(t('backoffice.roles_perm_load_error', 'Unable to load role permissions.'));

        const roleData = (await roleRes.json()) as RoleDetail;
        const catalogData = (await catRes.json()) as PermissionCatalogItem[];
        const rolePermKeys = (await permsRes.json()) as string[];

        setRole(roleData);
        setCatalog(catalogData);
        const next: Record<string, boolean> = {};
        for (const k of rolePermKeys) next[k] = true;
        setSelected(next);
        lastSavedSelectionRef.current = [...rolePermKeys].sort().join('\u0000');
        hasHydratedSelection.current = true;
      } catch (e) {
        setError(e instanceof Error ? e.message : t('backoffice.roles_load_error', 'Unable to load role.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [authorizedFetch, roleId, t]);

  const toggle = (key: string) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setGroupAll = (groupKey: string, value: boolean) => {
    setSelected((prev) => {
      const next = { ...prev };
      const groupPerms = grouped.find(([g]) => g === groupKey)?.[1] ?? [];
      for (const p of groupPerms) next[p.key] = value;
      return next;
    });
  };

  useEffect(() => {
    if (!roleId || !hasHydratedSelection.current || loading) return;
    if (role?.isSystem) return;
    if (selectedSignature === lastSavedSelectionRef.current) return;

    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(async () => {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const payloadKeys = [...selectedKeys];
      const payloadSignature = payloadKeys.join('\u0000');
      try {
        const res = await authorizedFetch(`${API_BASE}/rbac/roles/${roleId}/permissions`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ permissionKeys: payloadKeys }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { message?: string }).message
              || t('backoffice.roles_perm_save_error', 'Unable to save permissions.'),
          );
        }
        lastSavedSelectionRef.current = payloadSignature;
        setSuccess(t('backoffice.roles_perm_save_success', 'Permissions saved.'));
        if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
        successTimerRef.current = window.setTimeout(() => setSuccess(null), 2200);
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : t('backoffice.roles_perm_save_error', 'Unable to save permissions.'),
        );
      } finally {
        setSaving(false);
      }
    }, 450);

    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, [authorizedFetch, loading, role?.isSystem, roleId, selectedKeys, selectedSignature, t]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
  }, []);

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="workspace-page-title">
              {t('backoffice.roles_manage_title', 'Manage role')}
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              {role ? (
                <>
                  <span className="font-semibold text-slate-900">{role.name}</span>
                  {role.description ? ` — ${role.description}` : ''}
                </>
              ) : (
                t('common.loading', 'Loading…')
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/workspace/roles')}
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-slate-400"
            >
              {t('common.back', 'Back')}
            </button>
          </div>
        </section>

        {error && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {error}
          </p>
        )}

        <section className="workspace-panel">
          {loading ? (
            <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
          ) : (
            <div className="space-y-6">
              <div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('backoffice.roles_permission_search_placeholder', {
                    defaultValue: 'Search permissions (name, key, resource, action)',
                  })}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                />
              </div>

              {filteredGrouped.map(([groupKey, perms]) => {
                const anySelected = perms.some((p) => !!selected[p.key]);
                const allSelected = perms.every((p) => !!selected[p.key]);
                const isCollapsed = collapsedGroups[groupKey] ?? true;
                const toggleGroup = () =>
                  setCollapsedGroups((prev) => ({
                    ...prev,
                    [groupKey]: !(prev[groupKey] ?? true),
                  }));

                return (
                  <div
                    key={groupKey}
                    className="cursor-pointer rounded-xl border border-slate-100 bg-white/60 p-4"
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (
                        target.closest(
                          'button,input,label,a,select,textarea,[role="button"]',
                        )
                      ) {
                        return;
                      }
                      toggleGroup();
                    }}
                  >
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">
                          {formatGroupLabel(groupKey, t)}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {t('backoffice.roles_group_help', 'Select what this role can do in this area.')}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCollapsedGroups((prev) => ({
                              ...prev,
                              [groupKey]: !(prev[groupKey] ?? true),
                            }));
                          }}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-slate-400"
                        >
                          {isCollapsed
                            ? t('common.expand', 'Expand')
                            : t('common.collapse', 'Collapse')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setGroupAll(groupKey, true)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-slate-400"
                        >
                          {t('backoffice.roles_group_select_all', 'Select all')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setGroupAll(groupKey, false)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-slate-400"
                        >
                          {t('backoffice.roles_group_clear', 'Clear')}
                        </button>
                        <span
                          className={[
                            'ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
                            allSelected
                              ? 'bg-green-100 text-green-700'
                              : anySelected
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-600',
                          ].join(' ')}
                        >
                          {allSelected
                            ? t('backoffice.roles_group_all', 'All')
                            : anySelected
                              ? t('backoffice.roles_group_some', 'Some')
                              : t('backoffice.roles_group_none', 'None')}
                        </span>
                      </div>
                    </div>

                    {!isCollapsed ? (
                      <div className="grid gap-2 md:grid-cols-2">
                        {perms.map((p) => (
                          <label
                            key={p.key}
                            className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs hover:border-slate-200"
                          >
                            <input
                              type="checkbox"
                              checked={!!selected[p.key]}
                              onChange={() => toggle(p.key)}
                              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            />
                            <div>
                              <div className="font-semibold text-slate-900">
                                {labelForPermission(p, t)}
                              </div>
                              <div className="text-[11px] text-slate-500">
                                {(p.descriptionKey ? t(p.descriptionKey, { defaultValue: '' }) : '') || p.key}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
      {(saving || success) ? (
        <div className="pointer-events-none fixed bottom-5 right-5 z-[80]">
          {saving ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 shadow-lg">
              {t('common.saving', 'Saving…')}
            </div>
          ) : null}
          {!saving && success ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-lg">
              {success}
            </div>
          ) : null}
        </div>
      ) : null}
    </MainLayout>
  );
}

